(() => {
  const moneyFormat = (cents) => {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    const value = (cents / 100).toFixed(2);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return `£${value}`;
  };

  const cartAddUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart/add.js`;
  };

  const unpatchedFetch = (url, options) =>
    new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('hidden', '');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      let started = false;
      let settled = false;
      const finish = (cb, value) => {
        if (settled) return;
        settled = true;
        iframe.remove();
        cb(value);
      };
      const run = () => {
        if (started) return;
        started = true;
        const fetchFn = iframe.contentWindow && iframe.contentWindow.fetch;
        if (typeof fetchFn !== 'function') {
          finish(reject, new Error('Cart request failed'));
          return;
        }
        fetchFn
          .call(iframe.contentWindow, url, options)
          .then((res) =>
            res.text().then((text) => {
              const headers = new Headers();
              try {
                res.headers.forEach((value, key) => headers.set(key, value));
              } catch (err) {
                headers.set('Content-Type', 'application/json');
              }
              finish(
                resolve,
                new Response(text, {
                  status: res.status,
                  statusText: res.statusText,
                  headers,
                })
              );
            })
          )
          .catch((err) => finish(reject, err));
      };
      iframe.addEventListener('load', run, { once: true });
      document.documentElement.appendChild(iframe);
      try {
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') run();
      } catch (err) {
        /* iframe not ready yet */
      }
    });

  const addToCart = (formData) => {
    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    };
    const url = cartAddUrl();
    return unpatchedFetch(url, options).catch(() => fetch(url, options));
  };

  function initGallery(root) {
    const track = root.querySelector('[data-ll-pdp-track]');
    const slides = [...root.querySelectorAll('[data-ll-pdp-slide]')];
    const thumbs = [...root.querySelectorAll('[data-ll-pdp-thumb]')];
    if (!track || !slides.length) return;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    let index = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    let lockScroll = false;
    let unlockTimer = 0;

    const mark = (next) => {
      index = next;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === next);
      });
      thumbs.forEach((thumb, i) => {
        const on = i === next;
        thumb.classList.toggle('is-selected', on);
        thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      const activeThumb = thumbs[next];
      if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
        activeThumb.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      }
    };

    const scrollToIndex = (next, instant) => {
      const slide = slides[next];
      if (!slide || !isMobile()) return;
      lockScroll = true;
      window.clearTimeout(unlockTimer);
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: instant ? 'auto' : 'smooth',
      });
      unlockTimer = window.setTimeout(
        () => {
          lockScroll = false;
        },
        instant ? 50 : 400
      );
    };

    const selectIndex = (next, options = {}) => {
      const instant = Boolean(options.instant);
      const fromScroll = Boolean(options.fromScroll);
      const clamped = Math.max(0, Math.min(slides.length - 1, next));
      if (clamped === index && fromScroll) return;
      mark(clamped);
      if (!fromScroll) scrollToIndex(clamped, instant);
    };

    const indexFromScroll = () => {
      const left = track.scrollLeft;
      return slides.reduce(
        (best, slide, i) => {
          const dist = Math.abs(slide.offsetLeft - left);
          return dist < best.dist ? { i, dist } : best;
        },
        { i: 0, dist: Infinity }
      ).i;
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => selectIndex(i));
    });

    track.addEventListener(
      'scroll',
      () => {
        if (lockScroll || !isMobile()) return;
        selectIndex(indexFromScroll(), { fromScroll: true });
      },
      { passive: true }
    );

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      selectIndex(index + (event.key === 'ArrowRight' ? 1 : -1));
    });

    window.addEventListener(
      'resize',
      () => {
        if (!isMobile()) return;
        scrollToIndex(index, true);
      },
      { passive: true }
    );

    if (index > 0) {
      requestAnimationFrame(() => scrollToIndex(index, true));
    }

    root._llSelectMediaById = (mediaId) => {
      if (!mediaId) return;
      const next = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (next >= 0) selectIndex(next, { instant: true });
    };
  }

  function findVariant(variants, optionValue, optionIndex) {
    return variants.find((v) => {
      const opts = v.options || [];
      return String(opts[optionIndex] || '').toLowerCase() === String(optionValue).toLowerCase();
    });
  }

  function updatePrice(root, variant) {
    const priceEl = root.querySelector('[data-ll-pdp-price-current]');
    const compareEl = root.querySelector('[data-ll-pdp-price-compare]');
    const saleEl = root.querySelector('[data-ll-pdp-sale]');
    if (!priceEl || !variant) return;

    const price = variant.price;
    const compare = variant.compare_at_price;
    priceEl.textContent = variant.price_formatted || moneyFormat(price);

    if (compareEl) {
      if (compare && compare > price) {
        compareEl.textContent = variant.compare_at_price_formatted || moneyFormat(compare);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    if (saleEl) {
      if (compare && compare > price) {
        const pct = Math.round(((compare - price) / compare) * 100);
        saleEl.textContent = pct > 0 ? `SALE ${pct}%` : 'SALE';
        saleEl.hidden = false;
      } else {
        saleEl.textContent = '';
        saleEl.hidden = true;
      }
    }
  }

  function initSubscribeWidget(root, form) {
    const nativePlans = form.querySelector('[data-ll-pdp-plans]');
    let sellingPlanInput = form.querySelector('[data-ll-pdp-selling-plan]');
    if (!sellingPlanInput) {
      const bridge = document.createElement('div');
      bridge.className = 'appstle_sub_widget';
      bridge.setAttribute('data-ll-pdp-plan-bridge', '');
      bridge.hidden = true;
      bridge.setAttribute('aria-hidden', 'true');
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'radio';
      sellingPlanInput.name = 'selling_plan';
      sellingPlanInput.setAttribute('data-ll-pdp-selling-plan', '');
      bridge.appendChild(sellingPlanInput);
      form.appendChild(bridge);
    }
    const purchaseRadios = [...form.querySelectorAll('[data-ll-pdp-purchase]')];
    const frequency = form.querySelector('[data-ll-pdp-frequency]');

    const appstlePresent = () =>
      Boolean(
        form.querySelector(
          '.appstle_subscription_wrapper, #appstle_subscription_widget0, .appstle_sub_widget:not([data-ll-pdp-plan-bridge])'
        )
      );

    const syncNativeVisibility = () => {
      const present = appstlePresent();
      if (nativePlans) nativePlans.hidden = present;
      if (!sellingPlanInput) return;
      if (present) {
        sellingPlanInput.checked = false;
        sellingPlanInput.removeAttribute('name');
      } else {
        sellingPlanInput.setAttribute('name', 'selling_plan');
      }
    };

    syncNativeVisibility();
    const observer = new MutationObserver(syncNativeVisibility);
    observer.observe(form, { childList: true, subtree: true });

    const setSellingPlan = (value) => {
      if (!sellingPlanInput) return;
      sellingPlanInput.value = value || '';
      sellingPlanInput.checked = Boolean(value);
    };

    const syncPurchase = () => {
      if (appstlePresent()) return;
      const selected = purchaseRadios.find((r) => r.checked);
      const mode = selected ? selected.value : 'onetime';
      purchaseRadios.forEach((radio) => {
        const plan = radio.closest('[data-ll-pdp-plan]');
        if (plan) plan.classList.toggle('is-selected', radio.checked);
      });
      if (mode === 'subscribe') {
        setSellingPlan(frequency ? frequency.value : '');
      } else {
        setSellingPlan('');
      }
    };

    purchaseRadios.forEach((radio) => radio.addEventListener('change', syncPurchase));
    if (frequency) {
      frequency.addEventListener('change', () => {
        syncPurchase();
        if (root._llCurrentVariant && root._llUpdateSubscribePrices) {
          root._llUpdateSubscribePrices(root._llCurrentVariant);
        }
      });
    }
    syncPurchase();

    root._llUpdateSubscribePrices = (variant) => {
      if (!variant) return;
      const oneTime = form.querySelector('[data-ll-onetime-price]');
      const subPrice = form.querySelector('[data-ll-sub-price]');
      const subCompare = form.querySelector('[data-ll-sub-compare]');
      if (oneTime) oneTime.textContent = variant.price_formatted || moneyFormat(variant.price);
      if (subCompare) subCompare.textContent = variant.price_formatted || moneyFormat(variant.price);

      const allocations = variant.selling_plan_allocations || [];
      const planId = frequency ? Number(frequency.value) : 0;
      const allocation =
        allocations.find((item) => Number(item.selling_plan_id) === planId) || allocations[0];
      if (subPrice) {
        subPrice.textContent = allocation
          ? allocation.price_formatted || moneyFormat(allocation.price)
          : variant.price_formatted || moneyFormat(variant.price);
      }
    };

    return { syncPurchase };
  }

  function initVariantForm(root) {
    const form = root.querySelector('[data-ll-pdp-form]');
    const jsonEl = root.querySelector('[data-ll-pdp-variants]');
    if (!form || !jsonEl) return;

    let variants = [];
    try {
      variants = JSON.parse(jsonEl.textContent || '[]');
    } catch (e) {
      variants = [];
    }

    const variantInput = form.querySelector('[name="id"]');
    const atc = form.querySelector('[data-ll-pdp-atc]');
    const errorEl = form.querySelector('[data-ll-pdp-error]');
    const optionIndex = Number(form.dataset.sizeOptionIndex || 0);
    const radios = [...form.querySelectorAll('[data-ll-pdp-size]')];
    const optionRadios = [...form.querySelectorAll('[data-ll-pdp-option]')];
    const allRadios = [...radios, ...optionRadios];
    const subscribe = initSubscribeWidget(root, form);

    /* Variant is resolved from every selected option combined
       (e.g. Size "3 x 50g" + Type "Decaf"), not from Size alone. */
    const selectedOptions = () => {
      const picked = {};
      const sizeChecked = radios.find((r) => r.checked) || radios[0];
      if (sizeChecked) picked[optionIndex] = sizeChecked.value;
      const groups = {};
      optionRadios.forEach((r) => {
        (groups[r.dataset.optionIndex] = groups[r.dataset.optionIndex] || []).push(r);
      });
      Object.entries(groups).forEach(([idx, group]) => {
        const checked = group.find((r) => r.checked) || group[0];
        if (checked) picked[idx] = checked.value;
      });
      return picked;
    };

    const findByOptions = (picked) =>
      variants.find((v) => {
        const opts = v.options || [];
        return Object.entries(picked).every(
          ([idx, value]) => String(opts[idx] || '').toLowerCase() === String(value).toLowerCase()
        );
      });

    const resolveVariant = () => {
      const picked = selectedOptions();
      const variant = findByOptions(picked);
      if (variant) return variant;
      const sizeChecked = radios.find((r) => r.checked) || radios[0];
      if (sizeChecked) {
        return (
          findVariant(variants, sizeChecked.value, optionIndex) ||
          variants.find((v) => String(v.id) === String(sizeChecked.dataset.variantId))
        );
      }
      return variants[0];
    };

    /* Grey out pills that have no variant in combination with the
       other groups' current selections. */
    const syncOptionAvailability = () => {
      const picked = selectedOptions();
      allRadios.forEach((radio) => {
        const idx = radio.hasAttribute('data-ll-pdp-option')
          ? radio.dataset.optionIndex
          : String(optionIndex);
        const trial = { ...picked, [idx]: radio.value };
        radio.disabled = !findByOptions(trial);
      });
    };

    const applyVariant = (variant) => {
      if (!variantInput) return;
      if (!variant) {
        if (atc) {
          atc.disabled = true;
          atc.textContent = 'SOLD OUT';
        }
        return;
      }
      root._llCurrentVariant = variant;
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      updatePrice(root, variant);
      if (root._llUpdateSubscribePrices) root._llUpdateSubscribePrices(variant);
      if (subscribe && subscribe.syncPurchase) subscribe.syncPurchase();

      allRadios.forEach((r) => {
        const label = r.closest('.ll-pdp__size');
        if (label) label.classList.toggle('is-selected', r.checked);
      });

      if (atc) {
        const available = Boolean(variant.available);
        atc.disabled = !available;
        atc.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }

      if (variant.featured_media_id && root._llSelectMediaById) {
        root._llSelectMediaById(variant.featured_media_id);
      }
      syncOptionAvailability();
    };

    allRadios.forEach((radio) => {
      radio.addEventListener('change', () => applyVariant(resolveVariant()));
    });

    applyVariant(resolveVariant());

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!variantInput || !atc || atc.disabled) return;

      const variantId = Number(variantInput.value);
      if (!variantId) return;

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      atc.disabled = true;
      const previousLabel = atc.textContent;
      atc.textContent = 'ADDING…';

      try {
        const drawer = document.querySelector('cart-drawer');
        const notification = document.querySelector('cart-notification');
        const cartUi = drawer || notification;
        const formData = new FormData(form);
        [...formData.keys()].forEach((key) => {
          if (key.indexOf('ll-pdp-') === 0 || key.indexOf('ll-purchase-') === 0) {
            formData.delete(key);
          }
        });
        if (!formData.get('quantity')) formData.set('quantity', '1');

        const realAppstle = form.querySelector(
          '.appstle_subscription_wrapper, #appstle_subscription_widget0, .appstle_sub_widget:not([data-ll-pdp-plan-bridge])'
        );
        const purchaseMode = (form.querySelector('[data-ll-pdp-purchase]:checked') || {}).value;
        const frequency = form.querySelector('[data-ll-pdp-frequency]');
        if (!realAppstle && purchaseMode === 'subscribe' && frequency && frequency.value) {
          formData.set('selling_plan', frequency.value);
        } else if (!realAppstle) {
          formData.delete('selling_plan');
        }

        let sectionIds = 'cart-drawer,cart-icon-bubble';
        if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
          sectionIds = cartUi
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean)
            .join(',');
        }
        formData.append('sections', sectionIds);
        formData.append('sections_url', window.location.pathname);
        if (cartUi && typeof cartUi.setActiveElement === 'function') {
          cartUi.setActiveElement(atc);
        }

        const response = await addToCart(formData);
        const data = await response.json();

        if (!response.ok || data.status) {
          const message = data.description || data.message || 'Unable to add to cart.';
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          }
          return;
        }

        if (drawer) {
          drawer.classList.remove('is-empty');
          drawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
        }

        if (cartUi && typeof cartUi.renderContents === 'function' && data.sections) {
          try {
            cartUi.renderContents(data);
          } catch (err) {
            if (drawer && typeof drawer.open === 'function') drawer.open(atc);
          }
        } else if (drawer && typeof drawer.open === 'function') {
          drawer.open(atc);
        } else {
          window.location.reload();
          return;
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'll-pdp', variantId, data },
          })
        );
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = 'Unable to add to cart. Please try again.';
          errorEl.hidden = false;
        }
      } finally {
        const current = resolveVariant();
        const available = current ? Boolean(current.available) : true;
        atc.disabled = !available;
        atc.textContent = available ? previousLabel || 'ADD TO CART' : 'SOLD OUT';
      }
    });
  }

  function initStoryAccordions(scope) {
    scope.querySelectorAll('[data-ll-pdp-story]').forEach((root) => {
      if (root.dataset.llBound === 'true') return;
      root.dataset.llBound = 'true';

      const items = [...root.querySelectorAll('[data-ll-pdp-story-item]')];
      if (!items.length) return;

      const setOpen = (index) => {
        items.forEach((item, i) => {
          const on = i === index;
          const btn = item.querySelector('[data-ll-pdp-story-trigger]');
          item.classList.toggle('is-open', on);
          if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
      };

      root._llOpenReviews = () => {
        const index = items.findIndex((item) => item.classList.contains('ll-pdp-story__item--reviews'));
        if (index < 0) return null;
        setOpen(index);
        return items[index];
      };

      items.forEach((item, i) => {
        const btn = item.querySelector('[data-ll-pdp-story-trigger]');
        if (!btn) return;
        btn.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          setOpen(isOpen ? -1 : i);
        });
      });

      setOpen(-1);
    });
  }

  function openStoryReviews() {
    const story = document.querySelector('[data-ll-pdp-story]');
    const target =
      (story && typeof story._llOpenReviews === 'function' && story._llOpenReviews()) ||
      document.getElementById('ll-pdp-reviews') ||
      document.querySelector('.ll-pdp-story__item--reviews');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function initReviewJump(root) {
    const badge = root.querySelector('[data-ll-pdp-reviews-jump], .ll-pdp__reviews');
    if (!badge || badge.dataset.llJumpBound === 'true') return;
    badge.dataset.llJumpBound = 'true';

    const go = (event) => {
      event.preventDefault();
      event.stopPropagation();
      openStoryReviews();
    };

    badge.addEventListener('click', go, true);
    badge.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openStoryReviews();
      }
    });
  }

  function initPdp(root) {
    if (!root || root.dataset.llBound === 'true') return;
    root.dataset.llBound = 'true';
    initGallery(root);
    initVariantForm(root);
    initReviewJump(root);
  }

  function initProductRail(root) {
    if (!root || root.dataset.llRailBound === 'true') return;
    root.dataset.llRailBound = 'true';

    const track = root.querySelector('[data-ll-pdp-rail-track]');
    const viewport = root.querySelector('[data-ll-pdp-rail-viewport]') || track;
    const dotsEl = root.querySelector('[data-ll-pdp-rail-dots]');
    if (!track || !dotsEl) return;

    const slides = [...track.children].filter((el) => el.classList.contains('ll-pdp-rail__slide'));
    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    let page = 0;
    let pointerStart = null;

    const render = () => {
      const mobile = isMobile();
      const buttons = [...dotsEl.querySelectorAll('.ll-pdp-rail__dot')];

      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', mobile && i === page);
        if (mobile && i !== page) {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        } else {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        }
      });

      buttons.forEach((btn, i) => {
        const active = i === page;
        btn.classList.toggle('is-active', active);
        if (active) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });

      dotsEl.hidden = !mobile || slides.length < 2;
    };

    const showPage = (nextPage) => {
      page = Math.max(0, Math.min(slides.length - 1, nextPage));
      render();
    };

    const rebuildDots = () => {
      dotsEl.replaceChildren();
      slides.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'll-pdp-rail__dot';
        btn.setAttribute('aria-label', `Product ${i + 1} of ${slides.length}`);
        btn.addEventListener('click', () => showPage(i));
        dotsEl.appendChild(btn);
      });
      render();
      requestAnimationFrame(() => root.classList.add('is-ready'));
    };

    viewport.addEventListener('pointerdown', (event) => {
      if (!isMobile() || event.button > 0) return;
      pointerStart = event.clientX;
    });

    viewport.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const distance = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) < 40) return;
      showPage(page + (distance < 0 ? 1 : -1));
    });

    viewport.addEventListener('pointercancel', () => {
      pointerStart = null;
    });

    window.addEventListener('resize', render, { passive: true });
    rebuildDots();
  }

  const boot = () => {
    document.querySelectorAll('[data-ll-pdp]').forEach(initPdp);
    document.querySelectorAll('[data-ll-pdp-rail]').forEach(initProductRail);
    initStoryAccordions(document);
  };

  document.addEventListener('shopify:section:load', (event) => {
    const rail = event.target.querySelector('[data-ll-pdp-rail]');
    if (rail) {
      delete rail.dataset.llRailBound;
      initProductRail(rail);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
